import { Leaf, Drumstick, Sparkles } from "lucide-react";

export const AppetizersSection = () => {
  return (
    <section
      id="appetizers"
      className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-white"
      data-testid="appetizers-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Content */}
          <div className="space-y-8 lg:sticky lg:top-32">
            <div className="space-y-4">
              <p className="text-sm tracking-widest uppercase font-semibold text-orange-700">
                Made fresh. Served with care.
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-gray-800">
                Hors d'Oeuvres & Elevated Bites
              </h2>
            </div>

            <p className="text-lg md:text-xl leading-relaxed text-gray-600">
              Our hors d'oeuvres menu is designed for cocktail hours, networking events, and social gatherings where presentation matters as much as taste. Expect refined, bite-sized offerings that are easy to enjoy, visually appealing, and full of bold, familiar flavor.
            </p>

            <div className="pt-4 border-l-4 border-orange-700 pl-6">
              <p className="font-accent text-xl italic text-gray-700">
                "Thoughtfully portioned. Easy to serve. Impossible to forget."
              </p>
            </div>

            {/* Image */}
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl mt-8">
              <img
                src="https://images.unsplash.com/photo-1763553113344-b6a32fcd9ffa?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Artistic appetizer tray display"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>

          {/* Right - Categories */}
          <div className="space-y-6">
            {/* Vegetarian Category */}
            <div className="p-6 md:p-8 rounded-2xl bg-stone-50 border border-green-200 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Leaf className="w-6 h-6 text-green-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-2xl font-semibold text-gray-800">
                    Vegetarian
                  </h3>
                  <p className="text-sm text-green-700 font-medium mt-1">
                    Fresh, vibrant, and crowd-pleasing
                  </p>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-green-100" />
                  <span className="text-sm text-gray-700">Paneer Tikka Skewers</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-green-100" />
                  <span className="text-sm text-gray-700">Spinach & Cheese Cups</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-green-100" />
                  <span className="text-sm text-gray-700">Vegetable Samosas</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-green-100" />
                  <span className="text-sm text-gray-700">Corn & Pepper Bites</span>
                </div>
              </div>
            </div>

            {/* Non-Vegetarian Category */}
            <div className="p-6 md:p-8 rounded-2xl bg-stone-50 border border-orange-200 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Drumstick className="w-6 h-6 text-orange-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-2xl font-semibold text-gray-800">
                    Non-Vegetarian
                  </h3>
                  <p className="text-sm text-orange-700 font-medium mt-1">
                    Balanced, flavorful, and indulgent
                  </p>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-orange-100" />
                  <span className="text-sm text-gray-700">Chicken Tikka Bites</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-orange-100" />
                  <span className="text-sm text-gray-700">Lamb Kebab Sliders</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-orange-100" />
                  <span className="text-sm text-gray-700">Tandoori Prawns</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-orange-100" />
                  <span className="text-sm text-gray-700">Masala Fish Fingers</span>
                </div>
              </div>
            </div>

            {/* Indo-Fusion Category */}
            <div className="p-6 md:p-8 rounded-2xl bg-stone-50 border border-purple-200 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-purple-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-2xl font-semibold text-gray-800">
                    Indo-Fusion
                  </h3>
                  <p className="text-sm text-purple-700 font-medium mt-1">
                    Classic comfort with a modern twist
                  </p>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-purple-100" />
                  <span className="text-sm text-gray-700">Butter Chicken Tacos</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-purple-100" />
                  <span className="text-sm text-gray-700">Tikka Masala Bruschetta</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-purple-100" />
                  <span className="text-sm text-gray-700">Curry Puff Pastries</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-purple-100" />
                  <span className="text-sm text-gray-700">Chaat Canapes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppetizersSection;
